<script lang="ts">
  interface MoodVector {
    happy: number | null;
    sad: number | null;
    relaxed: number | null;
    aggressive: number | null;
    party: number | null;
    electronic: number | null;
    acoustic: number | null;
  }

  interface AcousticData {
    avg_bpm: number | null;
    avg_danceability: number | null;
    avg_energy: number | null;
    vote_count?: number;
    track_count?: number;
    // Sound DNA (present on `voted` when dna_track_count >= 3)
    avg_valence?: number | null;
    avg_acousticness?: number | null;
    avg_instrumentalness?: number | null;
    voice_ratio?: number | null;
    timbre_lean?: number | null;
    dna_track_count?: number;
    mood_vector?: MoodVector;
  }

  interface AcousticProfile {
    has_profile: boolean;
    voted: AcousticData | null;
    submitted: AcousticData | null;
    top_key: string | null;
  }

  const MOOD_ORDER = ['happy', 'sad', 'relaxed', 'aggressive', 'party', 'electronic', 'acoustic'] as const;

  let { acousticProfile }: { acousticProfile: AcousticProfile } = $props();

  const ap = $derived(acousticProfile.voted ?? acousticProfile.submitted);
  const isVoted = $derived(!!acousticProfile.voted);
  const sourceCount = $derived(
    ap ? ('vote_count' in ap && ap.vote_count != null ? ap.vote_count : (ap.track_count ?? 0)) : 0,
  );
  // Sound DNA only renders for the richer voted profile (the API gates it on dna_track_count >= 3).
  const dna = $derived(isVoted && ap?.mood_vector ? ap : null);
  const moodBars = $derived(
    dna?.mood_vector ? MOOD_ORDER.map((k) => ({ label: k, value: dna.mood_vector![k] ?? 0 })) : [],
  );
</script>

{#if acousticProfile.has_profile && ap}
  <div class="sound-section">
    <h2>Your Sound</h2>
    <div class="sound-grid">
      {#if ap.avg_bpm}
        <div class="sound-stat">
          <span class="sound-label">Avg BPM</span>
          <span class="sound-value">{Math.round(ap.avg_bpm)}</span>
        </div>
      {/if}
      {#if acousticProfile.top_key}
        <div class="sound-stat">
          <span class="sound-label">Top Key</span>
          <span class="sound-value">{acousticProfile.top_key}</span>
        </div>
      {/if}
      {#if ap.avg_danceability != null}
        <div class="sound-stat">
          <span class="sound-label">Danceability</span>
          <div class="sound-bar">
            <div class="sound-fill" style="width: {ap.avg_danceability * 100}%"></div>
          </div>
        </div>
      {/if}
      {#if ap.avg_energy != null}
        <div class="sound-stat">
          <span class="sound-label">Energy</span>
          <div class="sound-bar">
            <div class="sound-fill sound-fill--energy" style="width: {ap.avg_energy * 100}%"></div>
          </div>
        </div>
      {/if}
    </div>
    {#if dna}
      <div class="dna">
        <h3 class="dna-title">Your Sound DNA</h3>
        <div class="mood-spectrum">
          {#each moodBars as m (m.label)}
            <div class="mood-row">
              <span class="mood-label">{m.label}</span>
              <div class="sound-bar">
                <div class="sound-fill" style="width: {m.value * 100}%"></div>
              </div>
            </div>
          {/each}
        </div>
        <div class="sound-grid">
          {#if dna.avg_valence != null}
            <div class="sound-stat">
              <span class="sound-label">Positivity</span>
              <div class="sound-bar">
                <div class="sound-fill" style="width: {dna.avg_valence * 100}%"></div>
              </div>
            </div>
          {/if}
          {#if dna.avg_acousticness != null}
            <div class="sound-stat">
              <span class="sound-label">Acoustic</span>
              <div class="sound-bar">
                <div class="sound-fill" style="width: {dna.avg_acousticness * 100}%"></div>
              </div>
            </div>
          {/if}
          {#if dna.avg_instrumentalness != null}
            <div class="sound-stat">
              <span class="sound-label">Instrumental</span>
              <div class="sound-bar">
                <div class="sound-fill" style="width: {dna.avg_instrumentalness * 100}%"></div>
              </div>
            </div>
          {/if}
          {#if dna.voice_ratio != null}
            <div class="sound-stat">
              <span class="sound-label">Vocal</span>
              <div class="sound-bar">
                <div class="sound-fill" style="width: {dna.voice_ratio * 100}%"></div>
              </div>
            </div>
          {/if}
          {#if dna.timbre_lean != null}
            <div class="sound-stat">
              <span class="sound-label">Timbre · dark ↔ bright</span>
              <div class="sound-bar">
                <div class="sound-fill sound-fill--energy" style="width: {((dna.timbre_lean + 1) / 2) * 100}%"></div>
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}
    <p class="sound-source">
      Based on {sourceCount} track{sourceCount !== 1 ? 's' : ''}
      {isVoted ? 'you voted on' : 'you submitted'}
    </p>
  </div>
{/if}

<style>
  .sound-section {
    margin-top: 2rem;
  }
  .sound-section h2 {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }
  .sound-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
  .sound-stat {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .sound-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--text-muted);
    letter-spacing: 0.05em;
  }
  .sound-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text);
  }
  .sound-bar {
    height: 6px;
    background: var(--surface-2);
    border-radius: 3px;
    overflow: hidden;
  }
  .sound-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--purple);
    transition: width 0.6s ease;
  }
  .sound-fill--energy {
    background: var(--pink);
  }
  .dna {
    margin-top: 1.25rem;
  }
  .dna-title {
    font-size: 0.85rem;
    margin: 0 0 0.5rem;
    color: var(--text);
  }
  .mood-spectrum {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-bottom: 0.75rem;
  }
  .mood-row {
    display: grid;
    grid-template-columns: 5.5rem 1fr;
    align-items: center;
    gap: 0.5rem;
  }
  .mood-label {
    font-size: 0.7rem;
    text-transform: capitalize;
    color: var(--text-muted);
  }
  .sound-source {
    font-size: 0.7rem;
    color: var(--text-muted);
    margin-top: 0.5rem;
  }
  @media (max-width: 480px) {
    .sound-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
